import { AxiosResponse } from "axios";
import { ICustomerDetail, ICustomerNested, IGuestNested, ISetCustomerData } from "@interfaces";
import { eighteenYearsAgoISO } from "src/Utilities/dateOfBirth";

/**
 * Address-book helpers (MongoDB-backed saved travellers).
 *
 * Saved travellers are "guests" (`IGuestNested`, snake_case, Mongo `_id.$oid`).
 * They are READ from `GET cart/customers` (which returns the user's profile +
 * `guests`) and WRITTEN via `POST cart/guests` (create-or-update) /
 * `DELETE cart/guests/{guestId}`. These pure helpers convert between the server
 * shape and the editable form draft and pull the guests list out of the
 * (slightly variable) customers response envelope.
 */

/** Editable form shape for a single saved traveller. */
export interface GuestDraft {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    email: string;
    phone_number: string;
    post_code: string;
    country_code: string;
}

export const emptyGuest = (): GuestDraft => ({
    first_name: "",
    last_name: "",
    // Adults are the common case — default to the same date 18 years ago.
    date_of_birth: eighteenYearsAgoISO(),
    email: "",
    phone_number: "",
    post_code: "",
    country_code: "",
});

export const guestToDraft = (g: IGuestNested): GuestDraft => ({
    first_name: g.first_name ?? "",
    last_name: g.last_name ?? "",
    date_of_birth: g.date_of_birth ?? "",
    email: g.email ?? "",
    phone_number:
        g.phone_number === null || g.phone_number === undefined ? "" : String(g.phone_number),
    post_code: g.post_code ?? "",
    country_code: g.country_code ?? "",
});

/** Mongo id used by `DELETE cart/guests/{guestId}` and to mark an update. */
export const guestId = (g: IGuestNested): string | undefined => g._id?.$oid;

export const guestDisplayName = (g: Pick<IGuestNested, "first_name" | "last_name">): string =>
    [g.first_name, g.last_name].filter(Boolean).join(" ").trim();

type CustomersEnvelope = {
    guests?: IGuestNested[];
    travellers?: IGuestNested[];
    customer?: ICustomerNested;
    user_id?: string;
};

const unwrap = (
    resp?: AxiosResponse,
): CustomersEnvelope | IGuestNested[] | undefined => resp?.data?.data ?? resp?.data;

/** Pull the saved-traveller list out of a `GET cart/customers` response. */
export const extractGuests = (resp?: AxiosResponse): IGuestNested[] => {
    const d = unwrap(resp);
    if (Array.isArray(d)) return d;
    return d?.guests ?? d?.travellers ?? [];
};

/**
 * Best-effort `user_id` for the logged-in account, so create payloads can stamp
 * it. The backend derives the real owner from the auth token, so this is purely
 * a convenience and may be `undefined` (e.g. before the first traveller exists).
 */
export const extractUserId = (resp?: AxiosResponse): string | undefined => {
    const d = unwrap(resp);
    if (Array.isArray(d)) return d[0]?.user_id;
    return d?.customer?.user_id ?? d?.user_id ?? d?.guests?.[0]?.user_id;
};

/**
 * Address-book travellers surfaced as checkout contact suggestions.
 *
 * A saved guest has only a Mongo `_id.$oid`, not a numeric redeemer id, so it
 * cannot be assigned to a cart directly. We give guest-only suggestions a
 * prefixed synthetic id (`ab:<oid>`) so the checkout selection handler can tell
 * them apart from real redeemers and create a redeemer on-select before assigning.
 */
export const ADDRESS_BOOK_SUGGESTION_PREFIX = "ab:";

export const isAddressBookSuggestionId = (id: unknown): boolean =>
    typeof id === "string" && id.startsWith(ADDRESS_BOOK_SUGGESTION_PREFIX);

const guestPhoneNumber = (g: IGuestNested): number => {
    const n = Number(g.phone_number);
    return Number.isFinite(n) ? n : 0;
};

/** Convert an address-book guest into a checkout suggestion (`ICustomerDetail`). */
export const guestToSuggestion = (g: IGuestNested): ICustomerDetail => ({
    title: "",
    customer_index: 0,
    first_name: g.first_name ?? "",
    last_name: g.last_name ?? "",
    date_of_birth: g.date_of_birth ?? "",
    phone_number: guestPhoneNumber(g),
    email: g.email ?? "",
    postal_code: g.post_code ?? "",
    country_code: g.country_code ?? "",
    id: `${ADDRESS_BOOK_SUGGESTION_PREFIX}${guestId(g) ?? ""}`,
    redeemer_id: null,
    name: guestDisplayName(g),
    is_primary: 0,
    guest: g,
});

/** Build the create-redeemer payload for an address-book guest. */
export const guestToSetCustomerData = (
    g: IGuestNested,
    customerIndex = 0,
): ISetCustomerData => ({
    title: "",
    firstName: g.first_name ?? "",
    lastName: g.last_name ?? "",
    dateOfBirth: g.date_of_birth ?? "",
    email: g.email ?? "",
    customerIndex,
    phoneNumber:
        g.phone_number === null || g.phone_number === undefined ? "" : String(g.phone_number),
    postalCode: g.post_code ?? "",
    countryCode: g.country_code ?? "",
});

const norm = (s: unknown): string => String(s ?? "").trim().toLowerCase();

const identityKey = (first: unknown, last: unknown, dob: unknown): string =>
    `${norm(first)}|${norm(last)}|${norm(dob)}`;

const redeemerIdentityKey = (c: ICustomerDetail): string => {
    const details = c.is_primary ? c.customer : c.guest;
    return identityKey(
        details?.first_name ?? c.first_name,
        details?.last_name ?? c.last_name,
        details?.date_of_birth ?? c.date_of_birth,
    );
};

/**
 * Combine existing cart redeemers with address-book guests, deduplicated by
 * name + date-of-birth. A redeemer always wins over a matching guest (it carries
 * the assignable numeric id), and duplicate guests collapse to one entry.
 */
export const mergeAddressBookSuggestions = (
    redeemers: ICustomerDetail[],
    guests: IGuestNested[],
): ICustomerDetail[] => {
    const seen = new Set(redeemers.map(redeemerIdentityKey));
    const guestOnly: ICustomerDetail[] = [];
    for (const g of guests) {
        // Only dedupe by name+DOB when we actually have a name; otherwise fall back
        // to the stable Mongo id so distinct-but-blank saved travellers aren't
        // collapsed into a single suggestion.
        const hasName = norm(g.first_name) !== "" && norm(g.last_name) !== "";
        const key = hasName
            ? identityKey(g.first_name, g.last_name, g.date_of_birth)
            : `oid:${guestId(g) ?? identityKey(g.first_name, g.last_name, g.date_of_birth)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        guestOnly.push(guestToSuggestion(g));
    }
    return [...redeemers, ...guestOnly];
};
